import { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  IconButton,
  Grid,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  careerDataApi,
  courseDataApi,
  getInstituteCourses,
} from "../api/dashboardApi";
import Loader from "./Loader/Loader";
import { authFetch } from "../../api";
import { useTheme, useMediaQuery } from "@mui/material";
import { useWishlist } from "../context/WishlistContext";
import { Send } from "lucide-react";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SAP_CourseCard from "../pages/courses/CourseCard";

const CourseCard = () => {
  const { wishlist, fetchWishlist } = useWishlist();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [careerData, setCareerData] = useState([]);
  const [courseData, setCourseData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    nextPage: null,
    prevPage: null,
  });
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const userId = localStorage.getItem("userId");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));
  const cardsPerPage = useMemo(() => {
    if (isXs) return 1;
    if (isSm) return 2;
    if (isMd) return 2;
    if (isLg) return 2;
    if (isXl) return 3;
    return 2;
  }, [isXs, isSm, isMd, isLg, isXl]);

  const isEmptyCourse = hasLoaded && tabValue === 0 && courseData.length === 0;

  const isEmptyCareer = hasLoaded && tabValue === 1 && careerData.length === 0;

  const handleAddWishlist = async (courseId) => {
    try {
      const response = await authFetch("course-wishlist/add-shortlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          course_id: [courseId],
        }),
      });
      if (response.status === 200) {
        fetchWishlist();
      }
    } catch (err) {
      console.error("Failed to add to wishlist:", err);
    }
  };

  const handleRemoveWishlist = async (courseId) => {
    try {
      const response = await authFetch("course-wishlist/add-shortlist", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          course_id: [courseId],
        }),
      });
      if (response.status === 200) {
        fetchWishlist();
      }
    } catch (err) {
      console.error("Failed to add to wishlist:", err);
    }
  };

  const handlePrev = async () => {
    if (tabValue === 0) {
      // ==== Course Tab with API pagination ====
      if (currentIndex - cardsPerPage >= 0) {
        setCurrentIndex((prev) => prev - cardsPerPage);
      } else if (pagination.prevPage) {
        await fetchCourses(pagination.currentPage - 1);
        setCurrentIndex(Math.max(0, courseData.length - cardsPerPage));
      }
    } else {
      // ==== Career Tab (static data) ====
      if (currentIndex - cardsPerPage >= 0) {
        setCurrentIndex((prev) => prev - cardsPerPage);
      }
    }
  };

  const handleNext = async () => {
    if (tabValue === 0) {
      // ==== Course Tab with API pagination ====
      if (currentIndex + cardsPerPage < courseData.length) {
        setCurrentIndex((prev) => prev + cardsPerPage);
      } else if (pagination.nextPage) {
        await fetchCourses(pagination.currentPage + 1);
        setCurrentIndex(0);
      }
    } else {
      // ==== Career Tab (static data) ====
      if (currentIndex + cardsPerPage < careerData.length) {
        setCurrentIndex((prev) => prev + cardsPerPage);
      }
    }
  };

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
    setCurrentIndex(0);
  };

  const fetchCourses = async (page = 1) => {
    try {
      setLoading(true);

      const res = await getInstituteCourses(page);

      setCourseData(res.data?.data || []);
      setPagination(res.data?.pagination || {});
    } catch (err) {
      console.error("Error fetching courses:", err);
    } finally {
      setLoading(false);
    }
  };

  const FetchCareerData = async () => {
    try {
      setLoading(true);
      const response = await careerDataApi();
      setCareerData(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching careers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([FetchCareerData(), fetchCourses()]);
      setLoading(false);
      setHasLoaded(true);
    };

    init();
  }, []);

  return (
    <Grid
      sx={{
        p: 2,
        background: "#fff",
        borderRadius: "20px",
        boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
        position: "relative",
        width: "100%",
        minHeight: "500px",
      }}
    >
      <Grid container direction="column" maxWidth="100%">
        {/* Header */}
        <Grid
          container
          item
          xs={12}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item>
            <Grid
              container
              sx={{
                borderRadius: "40px",
                p: "6px",
                backgroundColor: "#F7EEF7",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleChange}
                TabIndicatorProps={{ style: { display: "none" } }}
                sx={{
                  minHeight: "unset",
                  "& .MuiTabs-flexContainer": {
                    gap: "8px",
                  },
                  "& .MuiTab-root": {
                    minHeight: "unset",
                    minWidth: "auto",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "14px",
                    lineHeight: "20px",
                    borderRadius: "20px",
                    padding: "6px 20px",
                    color: "#A41480",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                    fontFamily: "Inter",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#A41480",
                    color: "#fff !important",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.25)",
                  },
                }}
              >
                <Tab disableRipple label="Course" />
                <Tab disableRipple label="Career" />
              </Tabs>
            </Grid>
          </Grid>

          <Grid item>
            <a href="/sap/courses" style={{ textDecoration: "none" }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 800,
                  fontStyle: "normal",
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  verticalAlign: "middle",
                  cursor: "pointer",
                  mr: 1,
                  color: "#555555",
                }}
              >
                View All
              </Typography>
            </a>
          </Grid>
        </Grid>
        {loading ? (
          <Loader text="Loading data..." height="50vh" />
        ) : isEmptyCourse || isEmptyCareer ? (
          <Box
            sx={{
              width: "100%",
              minHeight: "280px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              color: "#777",
              p: 3,
              mt: 4,
            }}
          >
            {/* Heart Icon */}
            <Grid item>
              <svg
                width="74"
                height="74"
                viewBox="0 0 94 94"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="47"
                  cy="47"
                  r="47"
                  fill="#9D0385"
                  fill-opacity="0.06"
                />
                <path
                  d="M46.9974 62.3333C45.4641 61.1194 43.803 60.177 42.0141 59.5062C40.2252 58.8353 38.3724 58.4999 36.4557 58.4999C35.1141 58.4999 33.7964 58.6756 32.5026 59.027C31.2089 59.3784 29.971 59.8735 28.7891 60.5124C28.1182 60.8638 27.4714 60.8478 26.8484 60.4645C26.2255 60.0812 25.9141 59.5221 25.9141 58.7874V35.6916C25.9141 35.3402 26.0019 35.0048 26.1776 34.6853C26.3533 34.3659 26.6168 34.1263 26.9682 33.9666C28.4377 33.1999 29.971 32.6249 31.5682 32.2416C33.1655 31.8583 34.7946 31.6666 36.4557 31.6666C38.3085 31.6666 40.1214 31.9062 41.8943 32.3853C43.6672 32.8645 45.3682 33.5833 46.9974 34.5416V57.7332C48.6266 56.711 50.3356 55.9444 52.1245 55.4333C53.9134 54.9221 55.7182 54.6666 57.5391 54.6666C58.6891 54.6666 59.8151 54.7624 60.9172 54.9541C62.0193 55.1458 63.1293 55.4333 64.2474 55.8166V32.8166C64.7266 32.9763 65.1978 33.144 65.6609 33.3197C66.1241 33.4954 66.5793 33.711 67.0266 33.9666C67.378 34.1263 67.6415 34.3659 67.8172 34.6853C67.9929 35.0048 68.0807 35.3402 68.0807 35.6916V58.7874C68.0807 59.5221 67.7693 60.0812 67.1464 60.4645C66.5234 60.8478 65.8766 60.8638 65.2057 60.5124C64.0238 59.8735 62.7859 59.3784 61.4922 59.027C60.1984 58.6756 58.8807 58.4999 57.5391 58.4999C55.6224 58.4999 53.7696 58.8353 51.9807 59.5062C50.1918 60.177 48.5307 61.1194 46.9974 62.3333ZM50.8307 52.7499V34.5416L60.4141 24.9583V44.1249L50.8307 52.7499ZM43.1641 55.8645V36.8895C42.1099 36.4423 41.0158 36.0989 39.8818 35.8593C38.7477 35.6197 37.6057 35.4999 36.4557 35.4999C35.2738 35.4999 34.1238 35.6117 33.0057 35.8353C31.8877 36.0589 30.8016 36.3944 29.7474 36.8416V55.8645C30.8655 55.4492 31.9755 55.1458 33.0776 54.9541C34.1797 54.7624 35.3057 54.6666 36.4557 54.6666C37.6057 54.6666 38.7318 54.7624 39.8339 54.9541C40.9359 55.1458 42.046 55.4492 43.1641 55.8645Z"
                  fill="#9D0385"
                />
              </svg>
            </Grid>
            <Typography
              align="center"
              sx={{
                fontFamily: "inter, sans-serif",
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "100%",
                color: "#9D0385",
                mt: 2,
              }}
            >
              {tabValue === 0 ? "No Courses Found" : "No Careers Found"}
            </Typography>

            <Typography
              align="center"
              sx={{
                fontFamily: "inter, sans-serif",
                fontWeight: 400,
                fontSize: "21px",
                lineHeight: "100%",
                color: "#555555",
                maxWidth: 750,
                mx: "auto",
                mt: 2,
              }}
            >
              Unable to load data. Please try again later.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Carousel */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                overflow: "hidden",
                p: 2,
              }}
            >
              {/* Left Nav */}
              {!loading &&
                !(
                  (tabValue === 0 &&
                    currentIndex === 0 &&
                    !pagination.prevPage) ||
                  (tabValue === 1 && currentIndex === 0)
                ) && (
                  <IconButton
                    onClick={handlePrev}
                    disabled={
                      tabValue === 0
                        ? !pagination.prevPage && currentIndex === 0
                        : currentIndex === 0
                    }
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 0,
                      transform: "translateY(-50%)",
                      bgcolor: "#fff",
                      boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
                      "&:hover": { bgcolor: "#f2f2f2" },
                      zIndex: 10,
                    }}
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                )}

              {/* Cards Grid (slice visible) */}
              <Grid
                container
                spacing={2}
                wrap="nowrap"
                sx={{ overflow: "hidden" }}
              >
                {(tabValue === 0 ? courseData : careerData)
                  .slice(currentIndex, currentIndex + cardsPerPage)
                  .map((item, index) => {
                    const wishlisted = wishlist.includes(String(item.id));
                    return (
                      <Grid
                        item
                        key={item.id || index}
                        sx={{
                          flex: `0 0 ${100 / cardsPerPage}%`,
                          maxWidth: `${100 / cardsPerPage}%`,
                          boxSizing: "border-box",
                        }}
                      >
                        {tabValue === 0 ? (
                          <SAP_CourseCard course={item} />
                        ) : (
                          // <Card
                          //   key={item.id}
                          //   className="font-inter"
                          //   sx={{
                          //     borderRadius: "12px",
                          //     border: "1px solid #F9FAFB",
                          //     flexShrink: 0,
                          //     boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                          //     backgroundColor: "white",
                          //   }}
                          // >
                          //   <CardContent sx={{ p: 2 }}>
                          //     <Box
                          //       sx={{
                          //         backgroundColor: "#FFFFFF",
                          //         borderRadius: "12px",
                          //         border: "1px solid #E3D8F5",
                          //         boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
                          //         overflow: "hidden",
                          //         width: "100%",
                          //       }}
                          //     >
                          //       {/* Top Section */}
                          //       <Box
                          //         sx={{
                          //           display: "flex",
                          //           justifyContent: "space-between",
                          //           alignItems: "center",
                          //           padding: "12px 16px",
                          //           backgroundColor: "#FDF3FD",
                          //         }}
                          //       >
                          //         {/* Left Side - Icon + Title */}
                          //         <Box
                          //           sx={{
                          //             display: "flex",
                          //             alignItems: "center",
                          //             gap: 1,
                          //           }}
                          //         >
                          //           <img
                          //             src="/graduationCap.png"
                          //             alt="GC"
                          //             className="h-8 w-10"
                          //           />
                          //           <Typography
                          //             sx={{
                          //               fontWeight: 600,
                          //               fontSize: "16px",
                          //               color: "#1E1E1E",
                          //               lineHeight: "100%",
                          //             }}
                          //           >
                          //             {item.course_name}
                          //           </Typography>
                          //         </Box>

                          //         {/* Wishlist Button */}
                          //         <IconButton
                          //           size="small"
                          //           onClick={() =>
                          //             wishlisted
                          //               ? handleRemoveWishlist(item.id)
                          //               : handleAddWishlist(item.id)
                          //           }
                          //           sx={{
                          //             backgroundColor: "#FFFFFF",
                          //             border: "1px solid #E3D8F5",
                          //             boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
                          //             "&:hover": { backgroundColor: "#F4ECFF" },
                          //           }}
                          //         >
                          //           {wishlisted ? (
                          //             <FavoriteIcon sx={{ color: "#9C27B0" }} />
                          //           ) : (
                          //             <FavoriteBorderIcon
                          //               sx={{ color: "#9C27B0" }}
                          //             />
                          //           )}
                          //         </IconButton>
                          //       </Box>

                          //       {/* Bottom Gradient Section (Intake) */}
                          //       <Box
                          //         sx={{
                          //           background:
                          //             "linear-gradient(90deg, #FDF3FD 0%, #E6C6E6 100%)",
                          //           padding: "8px 16px",
                          //         }}
                          //       >
                          //         <Typography
                          //           sx={{
                          //             fontWeight: 600,
                          //             fontSize: "14px",
                          //             color: "#572575",
                          //             lineHeight: "100%",
                          //           }}
                          //         >
                          //           Intake:{" "}
                          //           {new Date(
                          //             0,
                          //             item.intake_month_from - 1
                          //           ).toLocaleString("en-US", {
                          //             month: "short",
                          //           })}{" "}
                          //           {item.intake_year_from}
                          //         </Typography>
                          //       </Box>
                          //     </Box>

                          //     {/* University + Country Card */}
                          //     <Card
                          //       sx={{
                          //         borderRadius: 2,
                          //         boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                          //         p: 1.5,
                          //         my: 2,
                          //         backgroundColor: "white",
                          //       }}
                          //     >
                          //       <Grid container spacing={1}>
                          //         <Grid item xs={12}>
                          //           <Grid
                          //             container
                          //             alignItems="center"
                          //             spacing={1}
                          //           >
                          //             <Grid item>
                          //               <Box
                          //                 component="img"
                          //                 src="/university.svg"
                          //                 alt="uni"
                          //                 sx={{ width: 14, height: 14 }}
                          //               />
                          //             </Grid>
                          //             <Grid item>
                          //               <Typography
                          //                 sx={{
                          //                   fontSize: "12px",
                          //                   color: "#313439",
                          //                 }}
                          //               >
                          //                 {item.university_name}
                          //               </Typography>
                          //             </Grid>
                          //           </Grid>
                          //         </Grid>

                          //         <Grid item xs={12}>
                          //           <Grid
                          //             container
                          //             alignItems="center"
                          //             spacing={1}
                          //           >
                          //             <Grid item>
                          //               <Box
                          //                 component="img"
                          //                 src="/location.svg"
                          //                 alt="location"
                          //                 sx={{ width: 14, height: 14 }}
                          //               />
                          //             </Grid>
                          //             <Grid item>
                          //               <Typography
                          //                 sx={{
                          //                   fontSize: "12px",
                          //                   color: "#313439",
                          //                 }}
                          //               >
                          //                 {item.country_name}
                          //               </Typography>
                          //             </Grid>
                          //           </Grid>
                          //         </Grid>
                          //       </Grid>
                          //     </Card>

                          //     {/* Fees Card */}
                          //     <Card
                          //       sx={{
                          //         borderRadius: 2,
                          //         boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                          //         p: 1.5,
                          //         mb: 2,
                          //         backgroundColor: "white",
                          //       }}
                          //     >
                          //       <Grid container>
                          //         <Grid item xs={12} sx={{ mb: 1 }}>
                          //           <Grid
                          //             container
                          //             alignItems="center"
                          //             spacing={1}
                          //           >
                          //             <Grid item>
                          //               <Box
                          //                 component="img"
                          //                 src="/money.png"
                          //                 alt="dolar"
                          //                 sx={{ width: 14, height: 14 }}
                          //               />
                          //             </Grid>
                          //             <Grid item>
                          //               <Typography
                          //                 sx={{
                          //                   fontWeight: 400,
                          //                   fontSize: "14px",
                          //                   lineHeight: "100%",
                          //                   color: "#572575",
                          //                 }}
                          //               >
                          //                 Fees
                          //               </Typography>
                          //             </Grid>
                          //           </Grid>
                          //         </Grid>

                          //         <Grid item xs={6} textAlign="start">
                          //           <Typography
                          //             fontSize="12px"
                          //             fontWeight={700}
                          //             color="#3F424C"
                          //           >
                          //             {item.currency_code}{" "}
                          //             {item.application_fee || 0}
                          //           </Typography>
                          //           <Typography fontSize="12px" color="#5F6570">
                          //             Application Fees
                          //           </Typography>
                          //         </Grid>
                          //         <Grid item xs={6} textAlign="start">
                          //           <Typography
                          //             fontSize="12px"
                          //             fontWeight={700}
                          //             color="#3F424C"
                          //           >
                          //             {item.currency_code}{" "}
                          //             {item.total_tution_fees || 0}
                          //           </Typography>
                          //           <Typography fontSize="12px" color="#5F6570">
                          //             Tuition Fees
                          //           </Typography>
                          //         </Grid>
                          //       </Grid>
                          //     </Card>

                          //     {/* Test Eligibility */}
                          //     <Card
                          //       sx={{
                          //         borderRadius: 2,
                          //         boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                          //         p: 1.5,
                          //         mb: 2,
                          //         backgroundColor: "white",
                          //       }}
                          //     >
                          //       <Grid
                          //         container
                          //         alignItems="center"
                          //         spacing={1}
                          //         sx={{ mb: 1 }}
                          //       >
                          //         <Grid item>
                          //           <Box
                          //             component="img"
                          //             src="/notes.png"
                          //             alt="dolar"
                          //             sx={{ width: 12, height: 18 }}
                          //           />
                          //         </Grid>
                          //         <Grid item>
                          //           <Typography
                          //             sx={{
                          //               fontWeight: 500,
                          //               fontSize: "14px",
                          //               lineHeight: "100%",
                          //               color: "#572575",
                          //             }}
                          //           >
                          //             Test eligibility
                          //           </Typography>
                          //         </Grid>
                          //       </Grid>

                          //       <Grid container spacing={1}>
                          //         {item.englishTests?.map((test, idx) => (
                          //           <Grid
                          //             item
                          //             xs={3}
                          //             key={idx}
                          //             textAlign="start"
                          //           >
                          //             <Typography
                          //               fontSize="12px"
                          //               fontWeight={700}
                          //               color="#3F424C"
                          //             >
                          //               {test.overall}
                          //             </Typography>
                          //             <Typography
                          //               fontSize="12px"
                          //               color="#5F6570"
                          //             >
                          //               {test.test_type}
                          //             </Typography>
                          //           </Grid>
                          //         ))}
                          //       </Grid>
                          //     </Card>

                          //     {/* Apply Button */}
                          //     <Button
                          //       variant="contained"
                          //       fullWidth
                          //       sx={{
                          //         flex: 1,
                          //         gap: 1,
                          //         mt: 1,
                          //         background: "#572575",
                          //         borderRadius: "12px",
                          //         textTransform: "none",
                          //         fontWeight: 600,
                          //         fontSize: "15px",
                          //         "&:hover": { background: "#572575" },
                          //       }}
                          //     >
                          //       Apply <Send size={20} />
                          //     </Button>
                          //   </CardContent>
                          // </Card>
                          // ================== Career Card Grid Item ==================
                          <Grid item key={item.id || index}>
                            <Card
                              sx={{
                                borderRadius: "12px",
                                border: "1px solid #F9FAFB",
                                boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                                backgroundColor: "white",
                              }}
                            >
                              <CardContent
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {/* Image */}
                                <Box
                                  component="img"
                                  src={
                                    item.image ||
                                    "https://images.unsplash.com/photo-1563298723-dcfebaa392e3"
                                  }
                                  alt={item.name}
                                  sx={{
                                    width: "100%",
                                    height: { xs: 120, sm: 140, md: 160 },
                                    borderRadius: "12px",
                                    objectFit: "cover",
                                    mb: 2,
                                  }}
                                />

                                {/* Title */}
                                <Typography
                                  fontWeight={700}
                                  fontSize={{
                                    xs: "14px",
                                    sm: "16px",
                                    md: "18px",
                                  }}
                                  sx={{
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                  }}
                                  fontFamily="Inter"
                                  color="#161719"
                                  mb={2}
                                >
                                  {item.name}
                                </Typography>

                                {/* Stats */}
                                <Grid container spacing={2} mb={2}>
                                  <Grid item xs={6}>
                                    <Card
                                      sx={{
                                        borderRadius: "12px",
                                        p: 1,
                                        boxShadow:
                                          "0px 2px 5px rgba(0,0,0,0.2)",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: 64,
                                      }}
                                      spacing={2}
                                    >
                                      <Grid
                                        container
                                        alignItems="center"
                                        spacing={1}
                                      >
                                        <Grid item>
                                          <img src="/lock.png" alt="lock" />
                                        </Grid>
                                        <Grid item>
                                          <Typography
                                            fontSize="14px"
                                            fontWeight={600}
                                          >
                                            {item.career_count}
                                          </Typography>
                                          <Typography
                                            fontSize="12px"
                                            color="text.secondary"
                                          >
                                            Career
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Card>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Card
                                      sx={{
                                        borderRadius: "12px",
                                        p: 1,
                                        boxShadow:
                                          "0px 2px 5px rgba(0,0,0,0.2)",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: 64,
                                      }}
                                      spacing={2}
                                    >
                                      <Grid
                                        container
                                        alignItems="center"
                                        spacing={1}
                                      >
                                        <Grid item>
                                          <img src="/group.png" alt="group" />
                                        </Grid>
                                        <Grid item>
                                          <Typography
                                            fontSize="14px"
                                            fontWeight={600}
                                          >
                                            {item.course_count}
                                          </Typography>
                                          <Typography
                                            fontSize="12px"
                                            color="text.secondary"
                                          >
                                            Courses
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Card>
                                  </Grid>
                                </Grid>

                                {/* Apply Button */}
                                <Button
                                  variant="contained"
                                  fullWidth
                                  sx={{
                                    mt: "auto",
                                    background: "#572575",
                                    borderRadius: "12px",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    fontSize: "15px",
                                    "&:hover": { background: "#4a178a" },
                                    fontFamily: "Inter",
                                  }}
                                >
                                  Explore more
                                  <img
                                    src="/arrow.png"
                                    alt="group"
                                    className="h-4 w-4 ml-2"
                                  />
                                </Button>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                      </Grid>
                    );
                  })}
              </Grid>
              {/* Right Nav */}
              {!loading && (
                <IconButton
                  onClick={handleNext}
                  disabled={
                    tabValue === 0
                      ? !pagination.nextPage &&
                        currentIndex + cardsPerPage >= courseData.length
                      : currentIndex + cardsPerPage >= careerData.length
                  }
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 0,
                    transform: "translateY(-50%)",
                    bgcolor: "#fff",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
                    "&:hover": { bgcolor: "#f2f2f2" },
                    zIndex: 10,
                  }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default CourseCard;
